// AttachDuplicateModal — link duplicate_of relations (separate from attach-child).

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { X, Copy, Loader2, AlertCircle, Search, ChevronDown } from 'lucide-react';
import { ticketService, isTicketApiSuccess } from '../../api/ticketService';

const DEBOUNCE_MS = 350;
const MIN_CHARS = 2;
const SEARCH_PAGE_SIZE = 20;

/** D7 — duplicate child cannot be linked when in these status_code values (matches backend). */
const DUPLICATE_LINK_BLOCKED_STATUS_CODES = new Set([
  'resolved',
  'closed',
  'supervisor_approved',
]);

const normalizeStatusCode = (statusCode) =>
  String(statusCode || '')
    .toLowerCase()
    .replace(/[\s-]+/g, '_');

const isDuplicateChildBlockedStatus = (statusCode) =>
  DUPLICATE_LINK_BLOCKED_STATUS_CODES.has(normalizeStatusCode(statusCode));

const friendlyError = (err) => {
  const fromResponse = err?.response?.data?.message || err?.data?.message;
  if (fromResponse) return fromResponse;
  if (err?.message) return err.message;
  return 'Unable to link duplicate ticket.';
};

const mapSearchRow = (row) => ({
  ticketId: row.ticketId ?? row.id,
  ticketNumber: row.ticketNumber ?? row.ticket_number,
  subject: row.subject,
  statusCode: row.statusCode ?? row.status_code,
  statusName: row.statusName ?? row.status_name,
  priorityName: row.priorityName ?? row.priority_name,
});

const AttachDuplicateModal = ({
  currentTicketId,
  currentTicketNumber,
  linkMode,
  isOpen,
  onClose,
  onLinked,
}) => {
  const [searchText, setSearchText] = useState('');
  const [results, setResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const debounceRef = useRef(null);
  const abortRef = useRef(null);
  const listboxId = 'attach-duplicate-listbox';

  const pickCanonical = linkMode === 'pick-canonical';

  const reset = () => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    setSearchText('');
    setResults([]);
    setSearchLoading(false);
    setSearchError(null);
    setSelected(null);
    setSubmitting(false);
    setFormError(null);
    setDropdownOpen(false);
  };

  const handleClose = () => {
    if (submitting) return;
    reset();
    onClose();
  };

  const runSearch = useCallback(
    async (q) => {
      const trimmed = (q || '').trim();
      if (trimmed.length < MIN_CHARS) {
        setResults([]);
        setSearchLoading(false);
        setSearchError(null);
        return;
      }
      if (abortRef.current) abortRef.current.abort();
      const ac = new AbortController();
      abortRef.current = ac;
      setSearchLoading(true);
      setSearchError(null);
      try {
        const res = await ticketService.searchLinkableDuplicateTickets(currentTicketId, {
          query: trimmed,
          page: 0,
          size: SEARCH_PAGE_SIZE,
          excludeDuplicateChildBlockedStatuses: !pickCanonical,
          signal: ac.signal,
        });
        if (abortRef.current !== ac) return;
        const apiData = res?.data;
        if (!isTicketApiSuccess(res)) {
          throw new Error(apiData?.message || 'Search failed');
        }
        const page = apiData?.data;
        const items = Array.isArray(page?.items) ? page.items.map(mapSearchRow) : [];
        setResults(
          items.filter((row) => {
            if (!row.ticketId || Number(row.ticketId) === Number(currentTicketId)) return false;
            if (!pickCanonical && isDuplicateChildBlockedStatus(row.statusCode)) return false;
            return true;
          }),
        );
      } catch (err) {
        if (err?.name === 'AbortError') return;
        setResults([]);
        setSearchError(friendlyError(err));
      } finally {
        if (abortRef.current === ac) setSearchLoading(false);
      }
    },
    [currentTicketId, pickCanonical],
  );

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (abortRef.current) abortRef.current.abort();
    };
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    reset();
  }, [isOpen, currentTicketId, linkMode]);

  useEffect(() => {
    if (!isOpen) return;
    const q = searchText;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if ((q || '').trim().length < MIN_CHARS) {
      setResults([]);
      setSearchLoading(false);
      setSearchError(null);
      return;
    }
    debounceRef.current = setTimeout(() => {
      debounceRef.current = null;
      runSearch(q);
    }, DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
    };
  }, [searchText, isOpen, runSearch]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError(null);
    if (!selected?.ticketId) {
      setFormError('Search and select a ticket.');
      return;
    }
    if (!pickCanonical && isDuplicateChildBlockedStatus(selected.statusCode)) {
      setFormError('This ticket cannot be linked as a duplicate in its current status.');
      return;
    }
    const canonicalId = pickCanonical ? selected.ticketId : currentTicketId;
    const duplicateId = pickCanonical ? currentTicketId : selected.ticketId;

    setSubmitting(true);
    try {
      const res = await ticketService.attachDuplicate(canonicalId, duplicateId);
      const apiData = res?.data;
      if (apiData?.success === false) {
        throw new Error(apiData?.message || 'Link failed');
      }
      if (!isTicketApiSuccess(res)) {
        throw new Error(apiData?.message || 'Link failed');
      }
      reset();
      onLinked?.(apiData?.data || apiData);
      onClose();
    } catch (err) {
      setFormError(friendlyError(err));
      setSubmitting(false);
    }
  };

  const pickResult = (row) => {
    if (!pickCanonical && isDuplicateChildBlockedStatus(row.statusCode)) return;
    setSelected(row);
    setSearchText(`${row.ticketNumber} — ${row.subject || ''}`.trim());
    setDropdownOpen(false);
    setFormError(null);
  };

  const clearSelection = () => {
    setSelected(null);
    setSearchText('');
    setResults([]);
    setDropdownOpen(true);
  };

  if (!isOpen) return null;

  const trimmedSearch = searchText.trim();
  const showDropdown =
    dropdownOpen &&
    (trimmedSearch.length >= MIN_CHARS || searchLoading || results.length > 0 || searchError);

  const modalTitle = pickCanonical
    ? 'Link as duplicate of another ticket'
    : 'Link another ticket as duplicate';

  const helpText = pickCanonical
    ? `This ticket (${currentTicketNumber || `#${currentTicketId}`}) will be marked as a duplicate of the ticket you select (canonical/original).`
    : `The ticket you select will be marked as a duplicate of this ticket (${currentTicketNumber || `#${currentTicketId}`}), which remains the canonical/original.`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-lg rounded-lg bg-white dark:bg-gray-800 shadow-xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <Copy className="h-5 w-5 text-amber-600 dark:text-amber-400" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{modalTitle}</h3>
          </div>
          <button
            type="button"
            onClick={handleClose}
            disabled={submitting}
            className="rounded p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
          <p className="text-sm text-gray-600 dark:text-gray-300">{helpText}</p>

          <div className="relative">
            <label htmlFor="attach-duplicate-search" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              {pickCanonical ? 'Find canonical ticket' : 'Find duplicate ticket'}
            </label>
            <div className="mt-1 relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                id="attach-duplicate-search"
                type="text"
                role="combobox"
                aria-expanded={dropdownOpen}
                aria-controls={listboxId}
                aria-autocomplete="list"
                value={searchText}
                onChange={(e) => {
                  setSearchText(e.target.value);
                  setSelected(null);
                  setDropdownOpen(true);
                }}
                onFocus={() => setDropdownOpen(true)}
                disabled={submitting}
                placeholder="e.g. TKT-000031 or keywords from the subject"
                autoComplete="off"
                className="block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 py-2 pl-9 pr-9 text-sm text-gray-900 dark:text-gray-100 shadow-sm focus:border-amber-500 focus:ring-amber-500"
              />
              <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            </div>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              At least {MIN_CHARS} characters. Results update after you stop typing (~{DEBOUNCE_MS} ms).
            </p>

            {showDropdown && (
              <ul
                id={listboxId}
                role="listbox"
                className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-900 py-1 text-sm shadow-lg"
              >
                {searchLoading && (
                  <li className="px-3 py-2 text-gray-500 dark:text-gray-400 flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin shrink-0" />
                    Searching…
                  </li>
                )}
                {!searchLoading && searchError && (
                  <li className="px-3 py-2 text-red-600 dark:text-red-400">{searchError}</li>
                )}
                {!searchLoading &&
                  !searchError &&
                  trimmedSearch.length >= MIN_CHARS &&
                  results.length === 0 && (
                    <li className="px-3 py-2 text-gray-500 dark:text-gray-400">No matching tickets.</li>
                  )}
                {!searchLoading &&
                  !searchError &&
                  results.map((row) => (
                    <li key={row.ticketId} role="presentation">
                      <button
                        type="button"
                        role="option"
                        aria-selected={selected?.ticketId === row.ticketId}
                        className="w-full text-left px-3 py-2 hover:bg-amber-50 dark:hover:bg-amber-900/30 focus:bg-amber-50 dark:focus:bg-amber-900/30 focus:outline-none"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => pickResult(row)}
                      >
                        <div className="font-mono text-xs font-semibold text-amber-800 dark:text-amber-200">
                          {row.ticketNumber}
                        </div>
                        <div className="text-gray-800 dark:text-gray-200 line-clamp-2">{row.subject}</div>
                        <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                          {[row.statusName || row.statusCode, row.priorityName].filter(Boolean).join(' · ')}
                        </div>
                      </button>
                    </li>
                  ))}
              </ul>
            )}
          </div>

          {selected && (
            <div className="rounded-md border border-amber-200 dark:border-amber-800 bg-amber-50/80 dark:bg-amber-950/40 px-3 py-2 text-sm">
              <div className="flex justify-between gap-2">
                <div>
                  <div className="font-mono text-xs font-semibold text-amber-900 dark:text-amber-100">
                    {selected.ticketNumber}
                  </div>
                  <div className="text-gray-800 dark:text-gray-100">{selected.subject}</div>
                </div>
                <button
                  type="button"
                  onClick={clearSelection}
                  disabled={submitting}
                  className="shrink-0 text-xs text-amber-800 dark:text-amber-200 hover:underline disabled:opacity-50"
                >
                  Clear
                </button>
              </div>
            </div>
          )}

          {formError && (
            <div className="flex items-start gap-2 rounded border border-red-200 dark:border-red-700 bg-red-50 dark:bg-red-900/20 px-3 py-2 text-sm text-red-700 dark:text-red-300">
              <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <div className="break-words">{formError}</div>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={handleClose}
              disabled={submitting}
              className="px-4 py-2 text-sm font-medium rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || !selected}
              className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md text-white bg-amber-600 hover:bg-amber-700 disabled:opacity-50"
            >
              {submitting && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}
              Link as Duplicate
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AttachDuplicateModal;
